import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import z from 'zod';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { ServerValidationError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { KeywordForm } from '@apple/features/keyword/KeywordForm';
import { deleteKeyword, saveKeywords } from '@apple/features/product/api/keywords';
import { productQueryKeys, productQueryOptions } from '@apple/features/product/queries/management';

export const Route = createFileRoute('/_authed/_admin/manage/keywords_/$keywordId')({
	parseParams: ({ keywordId }) => ({
		keywordId: z.string().parse(keywordId),
	}),
	beforeLoad: ({ params: { keywordId }, ...args }) => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ProductManagement.Read',
		});

		return {
			keywordQueryOptions: productQueryOptions.keyword(Number(keywordId)),
			keywordProductsQueryOptions: productQueryOptions.keywordProducts(Number(keywordId)),
		};
	},
	loader: ({ context: { queryClient, keywordProductsQueryOptions, keywordQueryOptions } }) => {
		void queryClient.prefetchQuery(keywordProductsQueryOptions);
		void queryClient.prefetchQuery(keywordQueryOptions);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation('keywords');
	const { keywordProductsQueryOptions, keywordQueryOptions } = Route.useRouteContext();
	const keywordProducts = useSuspenseQuery(keywordProductsQueryOptions);
	const keywordDetail = useSuspenseQuery(keywordQueryOptions);
	const queryClient = useQueryClient();
	const goBackToList = useBackOrNavigate();

	const saveKeywordProductsMutation = useMutation({
		mutationFn: saveKeywords,
		onSuccess: async () => {
			notifications.hide('keyword-save-success');
			notifications.show({
				id: 'keyword-save-success',
				color: 'green',
				message: t('save.success'),
				icon: <Success />,
			});
			await queryClient.invalidateQueries({ queryKey: productQueryKeys.keywords });
			await goBackToList('/manage/keywords');
		},
		onError: (error: Error) => {
			if (!(error instanceof ServerValidationError)) {
				notifications.show({
					message: t('common:error.generic'),
					icon: <ErrorIcon />,
					color: 'red',
					autoClose: 5000,
				});
			}
		},
	});

	const deleteKeywordMutation = useMutation({
		mutationFn: deleteKeyword,
		onSuccess: async () => {
			notifications.show({
				color: 'green',
				message: t('delete.success'),
				icon: <Success />,
			});
			await queryClient.invalidateQueries({ queryKey: productQueryKeys.keywords });
			await goBackToList('/manage/keywords');
		},
		onError: () => {
			notifications.show({
				color: 'red',
				message: t('common:error.generic'),
				icon: <ErrorIcon />,
			});
		},
	});

	const handleDeleteKeyword = useCallback(
		(keywordId: number) => {
			modals.openConfirmModal({
				title: t('delete.title'),
				labels: {
					confirm: t('common:buttons.confirm'),
					cancel: t('common:buttons.cancel'),
				},
				onConfirm: () => deleteKeywordMutation.mutate(keywordId),
				children: <Text>{t('delete.message')}</Text>,
			});
		},
		[deleteKeywordMutation, t],
	);

	return (
		<DetailsLayout title={t('title.edit')}>
			{keywordDetail.data && (
				<KeywordForm
					keyword={keywordDetail.data}
					initialProducts={keywordProducts.data}
					onChange={saveKeywordProductsMutation.mutateAsync}
					onDeleteKeyword={handleDeleteKeyword}
					isSaving={
						saveKeywordProductsMutation.isPending ||
						saveKeywordProductsMutation.isSuccess
					}
				/>
			)}
		</DetailsLayout>
	);
}
